import axios, { type AxiosResponse } from 'axios'
import type { BackendErrorPayload } from 'src/utils/error.utils'

export type QueryError = BackendErrorPayload<string> & {
  code?: string
  message?: string
  status?: number
  originalError?: unknown
}

const withOriginalError = (
  payload: QueryError,
  originalError: unknown,
): QueryError => {
  if ('originalError' in payload) {
    return payload
  }

  return Object.defineProperty(payload, 'originalError', {
    configurable: true,
    enumerable: false,
    value: originalError,
  })
}

export const normalizeQueryError = (error: unknown): QueryError => {
  const isAxiosError =
    axios.isAxiosError<BackendErrorPayload<string>>(error) ||
    (error &&
      typeof error === 'object' &&
      'isAxiosError' in error &&
      error.isAxiosError === true)

  if (isAxiosError) {
    const axiosError = error as {
      message: string
      response?: {
        data?: unknown
        status?: number
        statusText?: string
      }
    }
    const responseData = axiosError.response?.data

    if (
      responseData &&
      typeof responseData === 'object' &&
      'error' in responseData &&
      typeof responseData.error === 'string'
    ) {
      return withOriginalError(
        {
          ...(responseData as BackendErrorPayload<string>),
          status: axiosError.response?.status,
        },
        error,
      )
    }

    if (
      responseData &&
      typeof responseData === 'object' &&
      'code' in responseData &&
      typeof responseData.code === 'string'
    ) {
      return withOriginalError(
        {
          code: responseData.code,
          error: responseData.code,
          message:
            'message' in responseData &&
            typeof responseData.message === 'string'
              ? responseData.message
              : undefined,
          status: axiosError.response?.status,
        },
        error,
      )
    }

    return withOriginalError(
      {
        error: axiosError.response?.statusText ?? axiosError.message,
        status: axiosError.response?.status,
      },
      error,
    )
  }

  if (error && typeof error === 'object' && 'error' in error) {
    return withOriginalError(error as QueryError, error)
  }

  if (error instanceof Error) {
    return withOriginalError({ error: error.message }, error)
  }

  if (typeof error === 'string') {
    return { error }
  }

  return { error: 'Sorry, something went wrong. Please try again later.' }
}

export const getResponseData = async <T>(
  request: Promise<AxiosResponse<T>>,
): Promise<T> => {
  try {
    const response = await request
    return response.data
  } catch (error) {
    throw normalizeQueryError(error)
  }
}

// Recovers the raw backend payload from QueryError.originalError, falling back to the normalized message for response-less failures (src/queries/AGENTS.md).
export const recoverBackendErrorPayload = (
  error: unknown,
): BackendErrorPayload<string> => {
  const queryError = error as QueryError
  const originalError = queryError?.originalError
  const responseData = axios.isAxiosError(originalError)
    ? originalError.response?.data
    : undefined

  if (
    responseData &&
    typeof responseData === 'object' &&
    'error' in responseData
  ) {
    return responseData as BackendErrorPayload<string>
  }

  return {
    error:
      queryError?.error ??
      'Sorry, something went wrong. Please try again later.',
  }
}
